import has from 'ramda/src/has';
import isNil from 'ramda/src/isNil';
import Campaigns, { CampaignTypes } from '../constants/Campaigns';
import Urls from '../constants/Urls';

// Raw API plan: the mapped FREE type also covers api_starter and api_pro.
const FREE_PLAN = 'free';
const PREVIEW_PARAM = 'campaign-preview';
const PREVIEW_OFF = 'off';
const PREVIEW_STORAGE_KEY = 'mangools-campaign-preview';

// Local and test builds inline other hosts; QA previews the banner on beta and production before the start date.
const PREVIEW_ALLOWED = /^https?:\/\/([a-z0-9-]+\.)*(mnglsbeta|mangools)\.com\/?$/.test(Urls.MANGOOLS_APP_HOST);
const toBanner = (campaign, text) => ({
  type: campaign.type,
  text,
  buttonText: campaign.buttonText,
  url: campaign.url
});

// Built once, so a caller that reads the clock on every store update gets the same object back.
const CAMPAIGN_BANNERS = Campaigns.map(campaign => ({
  campaign,
  banner: toBanner(campaign, campaign.text),
  lastDaysBanner: toBanner(campaign, campaign.lastDaysText)
}));
const bannersOf = type => CAMPAIGN_BANNERS.find(({
  campaign
}) => campaign.type === type);
const PREVIEW_BANNERS = {
  bf: bannersOf(CampaignTypes.BLACK_FRIDAY).banner,
  'bf-ending': bannersOf(CampaignTypes.BLACK_FRIDAY).lastDaysBanner
};
const isPreview = value => has(value, PREVIEW_BANNERS);
const toMilliseconds = createdAt => typeof createdAt === 'string' ? Date.parse(createdAt) : createdAt;
const readStoredPreview = () => {
  try {
    return window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};
const storePreview = preview => {
  try {
    if (isNil(preview)) {
      window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, preview);
    }
  } catch (e) {
    // ignore write failures (private mode, quota)
  }
};
class CampaignService {
  /**
   * Return the campaign banner a user sees at `now`, or null.
   *
   * @param {now: number} Current time in milliseconds.
   * @param {createdAt: number|string} Registration time in milliseconds or as an ISO string.
   * @param {preview: string|null} A state from `readPreview`; it replaces the date and the registration cutoff,
   * never the plan rule.
   * @return {Object|null} `{ type, text, buttonText, url }`.
   */
  static getBanner({
    now,
    loggedIn,
    plan,
    createdAt,
    preview
  }) {
    if (!loggedIn || plan !== FREE_PLAN) {
      return null;
    }
    if (isPreview(preview)) {
      return PREVIEW_BANNERS[preview];
    }
    const nowTs = Math.floor(now / 1000);
    const active = CAMPAIGN_BANNERS.find(({
      campaign
    }) => nowTs >= campaign.startTs && nowTs <= campaign.endTs);
    if (isNil(active)) {
      return null;
    }
    const createdAtMs = toMilliseconds(createdAt);
    if (!Number.isFinite(createdAtMs) || createdAtMs >= active.campaign.registrationCutoffTs * 1000) {
      return null;
    }
    return nowTs >= active.campaign.lastDaysStartTs ? active.lastDaysBanner : active.banner;
  }

  /**
   * Beta and production builds: the banner state forced by `?campaign-preview=bf|bf-ending`, kept in the tab's session
   * storage so it survives in-app navigation. `?campaign-preview=off` clears it.
   *
   * @return {string|null} The state, or null on other builds and without a preview.
   */
  static readPreview() {
    if (!PREVIEW_ALLOWED || typeof window === 'undefined') {
      return null;
    }
    const requested = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
    if (requested === PREVIEW_OFF) {
      storePreview(null);
      return null;
    }
    if (isPreview(requested)) {
      storePreview(requested);
      return requested;
    }
    const stored = readStoredPreview();
    return isPreview(stored) ? stored : null;
  }
}
export default CampaignService;